import React from 'react';
import { track } from '../../../../hooks/useMixpanel';

export type LexicalErrorBoundaryProps = {
  children: React.ReactNode;
  onError: (error: Error) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Error boundary component for Lexical editor that:
 * - Reports errors to Mixpanel
 * - Calls optional onError callback
 * - Renders null when an error occurs
 */
export class ErrorBoundary extends React.Component<
  LexicalErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: LexicalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report to Mixpanel
    track('lexical_editor_error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error);
    }

    // Log to console for development
    console.error('Lexical Editor Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render nothing when there's an error
      return null;
    }

    return this.props.children;
  }
}
